import type {
    IGroupChatDocument,
    IGroupChatMsg,
    IGroupChatMsgAddResponse,
    IGroupChatMsgDeleteResponse,
    IGroupChatMsgsDeleteAllResponse,
    IGroupChatMsgsGetResponse,
    IGroupChatUser,
    IGroupChatUsersGetResponse,
} from "@common/types/api/r2-group-chat-types";
import { GroupChatApiResult } from "@common/types/api/r2-group-chat-types";
import { OfficeService } from "@core/office/office-service/office-service";
import { OfficeSessionService } from "@core/office/office-session/office.session.service";
import { resolveMessageScopedDocumentId } from "@core/shared/office-document-id";
import { IOutlookSelectionSnapshot, OutlookHost } from "@core/shared/office-hosts/outlook-host";
import { encodeOutlookGroupId, resolveOutlookGroupDocument } from "@core/shared/outlook-conversation-id";
import { acquireOutlookGraphToken, resolveOutlookGraphGroupRoot } from "@core/shared/outlook-graph-group-root";
import type { IGuid } from "@core/types/chat.types";
import type { IGroupChatSendResult } from "@core/types/group-chat-message.types";
import { environment } from "@env";
import { CHAT_ERROR_NAME } from "@services/chat-error/chat-error.service";
import { logger } from "@services/logger/logger.service";
import { SessionCryptoService } from "@services/session-crypto/session-crypto.service";
import { describeError } from "@utils/error";
import { BehaviorSubject } from "rxjs";
import { buildGroupChatDocument } from "../office-group-chat-document";

/**
 * HTTP engine + raw message store for the document-scoped group chat. The
 * session transport selects encrypted or plain addin endpoints from the
 * environment flag. The adapter maps this raw state to the UI model.
 */
export class OfficeGroupChatV1
{
    public static readonly OUTLOOK_NATIVE_CONFIRMATION_DELAY_MS = 250;
    private static readonly PAGE_SIZE = 100;

    /** Raw messages for the active document, kept sorted oldest-first. */
    public $messages: BehaviorSubject<IGroupChatMsg[]> = new BehaviorSubject<IGroupChatMsg[]>([]);
    /** email -> user, accumulated from every msgs/get and users/get response. */
    public $users: BehaviorSubject<Map<string, IGroupChatUser>> = new BehaviorSubject<Map<string, IGroupChatUser>>(new Map());

    protected document: IGroupChatDocument | null = null;
    protected documentGeneration = 0;
    protected mailConsentRequired = false;

    // Pagination cursor for older messages (msgs/get) and participants (users/get).
    private oldestMsgId: number | null = null;
    private usersCursor: string | null = null;
    private hasMoreUsers = true;

    // Ids of messages deleted this session. Polling only ever *adds* messages, so
    // without this a just-deleted message would be resurrected by the next poll
    // (and the delete would look like it never applied) until the upstream service
    // catches up. Cleared on (re)load of a document.
    private deletedIds = new Set<number>();

    constructor(
        protected officeService: OfficeService,
        protected sessionService: OfficeSessionService,
        protected sessionCrypto: SessionCryptoService,
    ) { }

    protected async resolveDocument(): Promise<IGroupChatDocument | null>
    {
        this.document = null;
        this.mailConsentRequired = false;
        const generation = this.documentGeneration;
        const host = this.officeService.getHost();
        let document: IGroupChatDocument | null;
        if (host instanceof OutlookHost)
        {
            if (environment.outlook_group_graph_enabled)
            {
                const snapshot = host.captureSelection();
                const mailboxHint = Office.context.mailbox?.userProfile?.emailAddress ?? '';
                const anchorId = await this.resolveOutlookRoot(host, snapshot, mailboxHint,
                    () => generation === this.documentGeneration && this.isOutlookGroupActive());
                const id = anchorId ? await encodeOutlookGroupId(anchorId) : null;
                document = id && host.isSelectionCurrent(snapshot)
                    ? { vendor_id: 'microsoft', type: 'mail', id }
                    : snapshot.isCompose ? null : (await resolveOutlookGroupDocument(host, snapshot)).document;
            }
            else
            {
                // const snapshot = host.captureSelection();
                // const nativeResolution = await resolveOutlookGroupDocument(host, snapshot);
                // document = nativeResolution.document;
                // if ((nativeResolution.reason !== 'references' || !nativeResolution.document) && snapshot.hasItem
                //     && generation === this.documentGeneration && host.isSelectionCurrent(snapshot))
                // {
                //     await new Promise<void>(resolve => setTimeout(resolve, OfficeGroupChatV1.OUTLOOK_NATIVE_CONFIRMATION_DELAY_MS));
                //     if (generation !== this.documentGeneration || !host.isSelectionCurrent(snapshot))
                //     {
                //         return null;
                //     }
                //     const confirmation = await resolveOutlookGroupDocument(host, snapshot);
                //     if (confirmation.reason === 'references' && confirmation.document)
                //     {
                //         document = confirmation.document;
                //     }
                // }
                // if (!host.isSelectionCurrent(snapshot))
                // {
                //     return null;
                // }
                document = (await resolveOutlookGroupDocument(host)).document;
            }
        }
        else
        {
            const id = await resolveMessageScopedDocumentId(host);
            document = id ? buildGroupChatDocument(host, id) : null;
        }
        if (generation !== this.documentGeneration)
        {
            return null;
        }
        this.document = document;
        return document;
    }

    protected resolveOutlookRoot(
        host: OutlookHost,
        snapshot: IOutlookSelectionSnapshot,
        mailboxHint: string,
        isActive: () => boolean,
        interactiveConsent = false,
    ): Promise<string | null>
    {
        return resolveOutlookGraphGroupRoot(host, snapshot, mailboxHint, async (hint, active, verifySelectedItem) =>
        {
            const token = await acquireOutlookGraphToken(hint, active, undefined, interactiveConsent, undefined, verifySelectedItem);
            if (isActive())
            {
                this.mailConsentRequired = !token;
            }
            return token;
        }, isActive);
    }

    protected isOutlookGroupActive(): boolean
    {
        return true;
    }

    /** Load the most recent page; return whether a successful response was applied. */
    protected async loadInitialMessages(): Promise<boolean>
    {
        const generation = this.documentGeneration;
        const document = this.document ?? (this.officeService.getHost() instanceof OutlookHost ? null : await this.resolveDocument());
        if (generation !== this.documentGeneration)
        {
            return false;
        }
        if (!document)
        {
            this.$messages.next([]);
            return false;
        }

        const res = await this.fetchMessages(document, undefined, 'desc');
        if (generation !== this.documentGeneration)
        {
            return false;
        }
        if (res.result !== GroupChatApiResult.Success)
        {
            throw new Error(this.mapResultToMessage(res.result));
        }
        const msgs = (res.msgs ?? []).slice().sort(this.byChronological);
        this.mergeUsers(res.users ?? []);
        this.oldestMsgId = res.next_msg_id_from ?? (msgs.length ? msgs[ 0 ].id : null);
        this.$messages.next(msgs);
        return true;
    }

    /** Fetch any messages newer than what we already hold and merge them in. */
    protected async pollNewMessages(): Promise<number>
    {
        const generation = this.documentGeneration;
        const document = this.document ?? (this.officeService.getHost() instanceof OutlookHost ? null : await this.resolveDocument());
        if (!document)
        {
            throw new Error('GROUP_CHAT.NO_ACTIVE_DOCUMENT');
        }

        const res = await this.fetchMessages(document, undefined, 'desc');
        if (generation !== this.documentGeneration || this.document !== document)
        {
            return 0;
        }
        if (res.result !== GroupChatApiResult.Success)
        {
            throw new Error(this.mapResultToMessage(res.result));
        }
        this.mergeUsers(res.users ?? []);
        return this.mergeMessages(res.msgs ?? []);
    }

    /** Load an older page (for scroll-back). Returns how many were prepended. */
    protected async loadOlderMessages(): Promise<number>
    {
        const document = this.document;
        const generation = this.documentGeneration;
        if (!document || this.oldestMsgId === null)
        {
            return 0;
        }

        const res = await this.fetchMessages(document, this.oldestMsgId, 'desc');
        if (generation !== this.documentGeneration)
        {
            return 0;
        }
        const older = res.msgs ?? [];
        this.mergeUsers(res.users ?? []);
        this.oldestMsgId = res.next_msg_id_from ?? (older.length ? Math.min(...older.map(m => m.id)) : this.oldestMsgId);
        return this.mergeMessages(older);
    }

    protected async addMessage(text: string, parentId: number | null | undefined, traceID: IGuid): Promise<IGroupChatMsg>
    {
        const generation = this.documentGeneration;
        const document = this.document ?? (this.officeService.getHost() instanceof OutlookHost ? null : await this.resolveDocument());
        if (!document)
        {
            throw new Error('GROUP_CHAT.NO_ACTIVE_DOCUMENT');
        }

        const res = await this.post<IGroupChatMsgAddResponse>('/user/msg/add', {
            document,
            text,
            ...(parentId != null && parentId >= 0 ? { parent_id: parentId } : {}),
        }, traceID);

        if (res.result !== GroupChatApiResult.Success || !res.msg)
        {
            throw new Error(this.mapResultToMessage(res.result));
        }

        if (generation === this.documentGeneration)
        {
            this.mergeMessages([ res.msg ]);
        }
        return res.msg;
    }

    protected errorHandler(error: unknown, traceID: IGuid, timestamp: number): IGroupChatSendResult
    {
        return {
            isError: true,
            text: this.parseError(error),
            traceID,
            timestamp,
        };
    }

    protected async deleteMessagesRemote(ids: number[]): Promise<void>
    {
        const document = this.document;
        if (!document || ids.length === 0)
        {
            return;
        }

        // Tombstone first so an in-flight/next poll can't resurrect these, then
        // drop locally so the UI updates immediately; the network call is
        // best-effort and must not gate the visual removal.
        ids.forEach(id => this.deletedIds.add(id));
        this.removeMessagesLocally(ids);

        try
        {
            const res = await this.post<IGroupChatMsgDeleteResponse>('/user/msg/delete', {
                document,
                msg_ids: ids,
            });
            if (res.result !== GroupChatApiResult.Success)
            {
                logger.warn('groupChat: delete failed', res);
            }
        }
        catch (e)
        {
            logger.error('groupChat: delete failed', e);
        }
    }

    // delete/all removes only the current user's messages upstream, so we drop
    // just those locally (keeping other participants' messages) and tombstone
    // them against polling. The owning ids are resolved by the adapter, which
    // knows the current user's email.
    protected async deleteAllRemote(ownIds: number[]): Promise<void>
    {
        const document = this.document;

        ownIds.forEach(id => this.deletedIds.add(id));
        this.removeMessagesLocally(ownIds);

        if (!document)
        {
            return;
        }

        try
        {
            const res = await this.post<IGroupChatMsgsDeleteAllResponse>('/user/msgs/delete/all', { document });
            if (res.result !== GroupChatApiResult.Success)
            {
                logger.warn('groupChat: delete all failed', res);
            }
        }
        catch (e)
        {
            logger.error('groupChat: delete all failed', e);
        }
    }

    /** Load a page of participants (paginated via cursor). */
    protected async loadUsersPage(): Promise<void>
    {
        const generation = this.documentGeneration;
        const document = this.document ?? (this.officeService.getHost() instanceof OutlookHost ? null : await this.resolveDocument());
        if (!document || !this.hasMoreUsers)
        {
            return;
        }

        const res = await this.post<IGroupChatUsersGetResponse>('/users/get', {
            document,
            ...(this.usersCursor ? { cursor: this.usersCursor } : {}),
            page_size: OfficeGroupChatV1.PAGE_SIZE,
        });

        if (generation !== this.documentGeneration || res.result !== GroupChatApiResult.Success)
        {
            return;
        }

        this.mergeUsers(res.users ?? []);
        this.usersCursor = res.next_cursor ?? null;
        this.hasMoreUsers = Boolean(res.next_cursor);
    }

    protected resetPagination(): void
    {
        this.oldestMsgId = null;
        this.usersCursor = null;
        this.hasMoreUsers = true;
        this.deletedIds.clear();
        this.$users.next(new Map());
    }

    private async fetchMessages(
        document: IGroupChatDocument,
        msgIdFrom: number | undefined,
        order: 'asc' | 'desc',
    ): Promise<IGroupChatMsgsGetResponse>
    {
        return this.post<IGroupChatMsgsGetResponse>('/user/msgs/get', {
            document,
            ...(msgIdFrom !== undefined ? { msg_id_from: msgIdFrom } : {}),
            page_size: OfficeGroupChatV1.PAGE_SIZE,
            msg_order: order,
        });
    }

    private async post<T>(path: string, body: unknown, traceID?: IGuid): Promise<T>
    {
        const options = traceID
            ? { headers: { 'x-hs-request-id': traceID } }
            : undefined;
        return this.sessionCrypto.postAddin<unknown, T>(`/group-chat${ path }`, body, options);
    }

    // Insert any messages we don't already have, keep the store sorted, and
    // return how many were actually new.
    private mergeMessages(incoming: IGroupChatMsg[]): number
    {
        if (incoming.length === 0)
        {
            return 0;
        }

        const current = this.$messages.getValue();
        const byId = new Map(current.map(m => [ m.id, m ]));
        let added = 0;
        for (const msg of incoming)
        {
            // Never re-introduce a message deleted this session — the upstream
            // list may still include it until the delete propagates.
            if (this.deletedIds.has(msg.id))
            {
                continue;
            }
            if (!byId.has(msg.id))
            {
                added++;
            }
            byId.set(msg.id, msg);
        }

        if (added === 0)
        {
            return 0;
        }

        const merged = Array.from(byId.values()).sort(this.byChronological);
        this.$messages.next(merged);
        return added;
    }

    private removeMessagesLocally(ids: number[]): void
    {
        const drop = new Set(ids);
        const next = this.$messages.getValue().filter(m => !drop.has(m.id));
        this.$messages.next(next);
    }

    private mergeUsers(users: IGroupChatUser[]): void
    {
        if (users.length === 0)
        {
            return;
        }
        const map = new Map(this.$users.getValue());
        for (const user of users)
        {
            map.set(user.user_email, user);
        }
        this.$users.next(map);
    }

    private byChronological = (a: IGroupChatMsg, b: IGroupChatMsg): number =>
    {
        if (a.dt !== b.dt)
        {
            return a.dt - b.dt;
        }
        return a.id - b.id;
    };

    private mapResultToMessage(result: GroupChatApiResult): string
    {
        switch (result)
        {
            case GroupChatApiResult.FailTokenInvalid:
                return CHAT_ERROR_NAME.SESSION_NOT_FOUND;
            case GroupChatApiResult.FailTokenExpired:
                return CHAT_ERROR_NAME.SESSION_EXPIRED;
            case GroupChatApiResult.FailAccessDenied:
            case GroupChatApiResult.FailServiceTemporaryNotValid:
            case GroupChatApiResult.FailInvalidParameters:
                return CHAT_ERROR_NAME.UNKNOWN_ERROR;
            default:
                return CHAT_ERROR_NAME.UNKNOWN_ERROR;
        }
    }

    private parseError(error: unknown): string
    {
        const apiResult = this.extractApiResult(error);
        if (apiResult !== null)
        {
            return this.mapResultToMessage(apiResult);
        }

        if (error instanceof Error)
        {
            return error.message || CHAT_ERROR_NAME.UNKNOWN_ERROR;
        }

        return describeError(error) || CHAT_ERROR_NAME.UNKNOWN_ERROR;
    }

    private extractApiResult(error: unknown): GroupChatApiResult | null
    {
        if (!error || typeof error !== 'object')
        {
            return null;
        }

        const candidate = error as {
            error?: {
                details?: { result?: unknown; };
            };
        };
        const result = candidate.error?.details?.result;

        return typeof result === 'number' ? result as GroupChatApiResult : null;
    }
}
